// Cross-thread audience inventory. Each thread is surfaced as one Audience
// (v1: an Audience = a thread's cart of segments). This lives in hooks/ — not
// features/audience — so the audience feature can read the thread list without
// importing features/thread (the layer rule keeps thread -> audience one-way;
// the shared read is lifted here, per the rule's own guidance).

import { useMemo } from 'react'
import { useGetThreads } from '@/features/thread/queries'
import type { AudienceThread } from '@/lib/domain'

export interface UseAudienceInventory {
    audiences: AudienceThread[]
    isLoading: boolean
}

export function useAudienceInventory(): UseAudienceInventory {
    const { data: threads, isLoading } = useGetThreads()

    const audiences = useMemo<AudienceThread[]>(() => {
        if (!threads) return []
        return (
            threads
                .map(
                    (t): AudienceThread => ({
                        threadId: t.threadId,
                        name:
                            t.state.threadTitle?.trim() || 'Untitled audience',
                        lastUpdated: t.lastUpdated,
                        segments: t.state.audienceConfigurations ?? [],
                        datasets: t.state.datasets ?? [],
                    })
                )
                // An audience is its segments — hide threads with an empty cart from
                // the library.
                .filter((a) => a.segments.length > 0)
        )
    }, [threads])

    return { audiences, isLoading }
}
